import { SqlValue } from '@sqlite.org/sqlite-wasm'
import moment from 'moment'
import Rollbar from 'rollbar'

import { logout } from '../modules/apiclient'
import { API_BASE, APP_VERSION, IS_DEVELOPMENT, TRANSACTION_TTL } from '../utils/constants'
import { generateRandomID } from '../utils/numbers'
import { CacheFactory } from './CacheFactory'
import { ISQLExecutor } from './ISQLExecutor'
import { ProviderHandlersProxy, createProviderHandlers } from './ProviderSQLHandlers'
import {
    SQLBuilder,
    SQLWorkerEnvelope,
    WorkerMessageType,
    WorkerResponseMessage,
} from './SQLBuilder'
import { SQLTransactionManager } from './SQLTransactionManager'
import { SharedService } from './sql.sharedservice.js'
import SQLWorker from './sql.worker?worker'

type ResolverType = {
    uuid: string
    resolve: any
    reject: any
    timeoutId?: any
    singleRow?: boolean
    callstack?: string // used in debug mode to print the callstack of the caller opposed to the callstack of the worker
}

export type WorkerPromiseResolver = (value: any) => void
export type WorkerPromiseRejecter = (reason?: any) => void

export type WorkerRequest = {
    timeoutSeconds?: number
    singleRow?: boolean
    transactionCorrelationId?: string
    payload?: SQLWorkerEnvelope
    type: WorkerMessageType
}

export type WorkerRequestWithResolver = WorkerRequest & {
    timeoutId: any
    resolve: WorkerPromiseResolver
    reject: WorkerPromiseRejecter
}

export class SQLiteFactory implements ISQLExecutor {
    private static instance: SQLiteFactory | null = null
    public static getInstance() {
        if (SQLiteFactory.instance == null) {
            SQLiteFactory.instance = new SQLiteFactory()
        }
        return SQLiteFactory.instance
    }

    constructor() {
        this.worker = new SQLWorker()
        this.sharedService = new SharedService('PocketSuiteSharedService', createProviderHandlers)
        this.sharedService.activate()
        this.worker.onmessage = (event) => this.onMessageReceived(event)
        this.proxy = this.sharedService.proxy as ProviderHandlersProxy
    }

    public proxy: ProviderHandlersProxy

    private worker: Worker
    private sharedService: SharedService
    private resolvers: ResolverType[] = []

    // it's possible to make calls before any provider is established, resulting in port errors
    // this ensures that the provider's port exists before making any calls
    async waitForProvider() {
        await this.sharedService.providerPort
    }

    get rollbar(): Rollbar | null {
        return (window as any).pocketsuite_rollbar ?? null
    }

    // this is the only place we post to the worker, it should only be called by the Worker Queue
    postMessageWithResolver({
        type,
        payload,
        singleRow,
        timeoutId,
        resolve,
        reject,
    }: WorkerRequestWithResolver) {
        const uuid = generateRandomID()
        const callstack = IS_DEVELOPMENT ? new Error().stack : undefined // get the caller's callstack for better debugging in case of an error
        this.resolvers.push({ uuid, resolve, reject, timeoutId, singleRow, callstack })
        this.worker.postMessage({ uuid, type, payload })
    }

    onMessageReceived(event: MessageEvent<WorkerResponseMessage>) {
        const { type, payload, uuid, cacheRows, metaData, error } = event.data
        const resolver = this.resolvers.find((r) => r.uuid === uuid)

        if (resolver && resolver.timeoutId) clearTimeout(resolver.timeoutId) // remove timeout if it exists

        if (resolver && type !== 'log' && type !== 'error') {
            if (type === 'exec') {
                if (cacheRows?.length) {
                    CacheFactory.getInstance().update(...cacheRows)
                }
                resolver.resolve(!!resolver.singleRow ? payload[0] : payload)
            }
            else if (
                type === 'initialize' ||
                type === 'drop' ||
                type === 'create' ||
                type === 'start_transaction' ||
                type === 'end_transaction'
            ) {
                resolver.resolve()
            }
            else {
                resolver.reject(payload)
            }
            this.resolvers = this.resolvers.filter((r) => r.uuid !== uuid)

            const end = new Date().getTime()
            if (cacheRows?.length && payload?.length) {
                console.debug(
                    `SQL CALL ${metaData.fkStart - metaData.sqlStart}ms, TOTAL ${end - metaData.start}ms, FK SQL CALL ${metaData.sqlEnd - metaData.fkStart}ms`
                )
            }
            else if (payload?.length) {
                const end = new Date().getTime()
                console.debug(
                    `SQL CALL ${metaData.fkStart - metaData.sqlStart}ms, TOTAL ${end - metaData.start}ms`
                )
            }
        }
        else if (type == 'log') {
            console.debug(payload)
        }
        else if (type == 'error') {
            if (resolver?.callstack) {
                const callerStack = resolver.callstack.split('\n').slice(1).join('\n') // remove first line, not helpful in debugging
                error.stack = error.stack + '\n' + callerStack // append the callstack of the caller to the error stack
            }
            if (metaData) console.error(metaData)
            const rollbar = this.rollbar
            if (import.meta.env.MODE !== 'development' && rollbar) {
                rollbar.error('SQLite Error', error)
            }
            else {
                console.error(error)
            }

            if (metaData?.fatal) logout() // todo replace with new logout call when tested
        }
        else {
            console.error('Could not resolve, no resolver found', event.data) // should we throw an error on development?
        }
    }

    async willSync(me: string | undefined, dbReset: boolean) {
        if (dbReset) {
            console.log('dbReset Requested!')
            await this.dropDB()
            return
        }

        const statusSql = new SQLBuilder({
            columns: ['lastUser', 'appVersion', 'serverHost'],
            tables: ['status'],
        })

        const status = await this.executeSingleRowQuery(statusSql)
        if (APP_VERSION !== status?.appVersion) {
            console.log('appVersion Changed!')
            await this.dropDB()
            await this.createSqlDb()
        }
        else if (API_BASE !== status?.serverHost) {
            console.log('serverHost Changed!')
            await this.dropDB()
            await this.createSqlDb()
        }
        else if (status?.lastUser && me !== status?.lastUser) {
            console.log('lastUser Changed!')
            await this.dropDB()
            await this.createSqlDb()
        }

        const lastSyncSql = new SQLBuilder({
            columns: ['lastSyncDate'],
            tables: ['status'],
        })
        const result = await this.executeSingleRowQuery(lastSyncSql)
        return !!result?.lastSyncDate ? moment(result.lastSyncDate) : undefined
    }

    async didSync(me: string, version: string) {
        return this.executeUpdate(
            'UPDATE status SET version=?, appVersion=?, lastUser=?, lastSyncDate=CURRENT_TIMESTAMP',
            version,
            APP_VERSION,
            me
        )
    }

    async badgeCount() {
        const badgeCountSql = new SQLBuilder({
            columns: ['badgeCount'],
            tables: ['status'],
        })
        const result = await this.executeSingleRowQuery(badgeCountSql)
        return !!result?.badgeCount ? parseInt(Number(result.badgeCount).toString()) : undefined
    }

    async setBadgeCount(badgeCount: number) {
        return this.executeUpdate('UPDATE status SET badgeCount=?', badgeCount)
    }

    async roleBadgeCount() {
        const badgeCountSql = new SQLBuilder({
            columns: ['roleBadgeCount'],
            tables: ['status'],
        })
        const result = await this.executeSingleRowQuery(badgeCountSql)
        return !!result?.roleBadgeCount
            ? parseInt(Number(result.roleBadgeCount).toString())
            : undefined
    }

    async setRoleBadgeCount(roleBadgeCount: number) {
        return this.executeUpdate('UPDATE status SET roleBadgeCount=?', roleBadgeCount)
    }

    async executeSql(payload: SQLBuilder) {
        return this.sendSqlEnvelope(payload.serialize(), false)
    }

    async debug_executeRawSQL(rawSQL: string) {
        return this.sendSqlEnvelope(
            {
                columns: [],
                tables: [],
                joins: [],
                binds: [],
                fieldBinds: [],
                filters: [],
                orderBy: [],
                unions: [],
                sql: rawSQL,
            },
            false
        )
    }

    private async sendSqlEnvelope(payload: SQLWorkerEnvelope, singleRow: boolean) {
        return await this.proxy['sendSqlEnvelope']<any[]>(payload, singleRow)
    }

    async executeUpdate(sql: string, ...binds: SqlValue[]) {
        await this.sendSqlEnvelope(
            {
                ...new SQLBuilder({ binds: binds }).serialize(),
                sql,
            },
            false
        )
    }

    async executeSingleRowQuery(payload: SQLBuilder): Promise<any | undefined> {
        return this.sendSqlEnvelope(payload.serialize(), true)
    }

    async executeTransaction(
        callback: (txManager: SQLTransactionManager) => Promise<void>,
        timeout: number = TRANSACTION_TTL
    ) {
        return await new Promise<void>(async (resolve, reject) => {
            const txManager = new SQLTransactionManager()

            const timeoutId = setTimeout(() => {
                reject(JSON.stringify({ error: 'transaction_timeout' }))
            }, timeout)

            try {
                await txManager.begin()
                await callback(txManager)
            }
            catch (err: any) {
                if (err.error === 'transaction_timeout') {
                    try {
                        console.debug('rolling back tx', err)
                        await txManager.rollback()
                    }
                    catch {
                        // panic !!!
                        await logout()
                    }

                    throw err
                }

                throw err
            }
            finally {
                clearTimeout(timeoutId)
            }

            // SQL errors on commit automatically rollback within the worker
            await txManager.commit()
            resolve()
        })
    }

    async createSqlDb() {
        await this.waitForProvider()
        await this.proxy['createSqlDb']()
    }

    async initializeSql() {
        await this.waitForProvider()
        await this.proxy['initializeSql']()
    }

    async dropDB() {
        await this.waitForProvider()
        await this.proxy['dropDB']()
    }
}
